'use client';

import { Component, ErrorInfo, type ReactNode } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Button, Typography } from '@/primitives';
import { theme } from '@/lib/theme';
import { ErrorCowCard } from '@/components/utils/ErrorCowCard';

interface CriticalErrorBoundaryProps {
  children: ReactNode;
}

interface CriticalErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Fallback rendered when an unrecoverable error escapes the provider tree.
 * Mounts its own minimal ThemeProvider because this boundary sits ABOVE the
 * real provider stack — once it fires, the original ThemeProvider has been
 * unmounted along with the rest of the tree.
 */
function CriticalErrorFallback({ error }: { error?: Error }) {
  const handleReload = () => {
    window.location.reload();
  };

  const description = (
    <>
      <Typography
        component="span"
        variant="bodymedium"
        sx={{ display: 'block', mb: 2 }}
      >
        A critical error occurred while loading the application. Please try
        reloading the page.
      </Typography>
      {process.env.NODE_ENV === 'development' && error?.message && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: '8px',
            backgroundColor: 'action.hover',
            border: '1px solid',
            borderColor: 'divider',
            textAlign: 'left',
          }}
        >
          <Typography
            component="code"
            sx={{
              fontFamily: 'monospace',
              fontSize: '12px',
              wordBreak: 'break-word',
              color: 'text.primary',
            }}
          >
            {error.message}
          </Typography>
        </Box>
      )}
    </>
  );

  return (
    <ThemeProvider theme={theme} defaultMode="system">
      <CssBaseline />
      <ErrorCowCard
        title="Application Error"
        description={description}
        actions={
          <Button
            variant="contained"
            onClick={handleReload}
            sx={{ textTransform: 'none' }}
          >
            Reload Page
          </Button>
        }
      />
    </ThemeProvider>
  );
}

export class CriticalErrorBoundary extends Component<
  CriticalErrorBoundaryProps,
  CriticalErrorBoundaryState
> {
  state: CriticalErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): CriticalErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Critical error caught by boundary:', error, errorInfo);

    try {
      if (typeof window !== 'undefined' && (window as any).DD_RUM) {
        (window as any).DD_RUM.addError(error, errorInfo);
      }
    } catch {
      // Ignore errors when logging to Datadog
    }
  }

  render() {
    if (this.state.hasError) {
      return <CriticalErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}
